"use client";

import { createContext, useContext, ReactNode, useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Code, Search, Eye, Shield, Hammer } from "lucide-react";
import { INSIGHT_TYPES, INSIGHT_LANGUAGE } from "@/bryntum-lib/models/Insights";
import { useFileInsights } from "@/react/hooks/entityHooks/useFileInsights";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { Insights } from "@/bryntum-lib/models/Insights";
import { FormattedFile } from "../../../../context/EntityFileContext";
import { formatFile, getFileExtension } from "@/modern/utils";
import { uploadFile } from "@/network/network";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { toast } from "sonner";
import { BoardFile } from "@/bryntum-lib/models/BoardFile";
import { EntityRecord } from "@/types/entity";

export interface InsightMessage {
  id: string;
  personaId: string;
  personaName: string;
  content: string;
  timestamp: Date;
}

export interface Persona {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  color: string;
  avatarUrl?: string | null;
}

// Map insight types to icons
const INSIGHT_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  analyze: Search,
  execute: Code,
  review: Eye,
  siteSafety: Shield,
  workmanship: Hammer
};

// Map insight types to colors
const INSIGHT_TYPE_COLORS: Record<string, string> = {
  analyze: "bg-blue-600",
  execute: "bg-green-600",
  review: "bg-purple-600",
  siteSafety: "bg-red-600",
  workmanship: "bg-orange-600"
};

interface AnalyzeFileContextValue {
  // State
  messages: InsightMessage[];
  selectedPersonaId: string | null;
  isTyping: boolean;
  typingPersona: Persona | null;
  typingPersonas: Set<string>; // Track multiple typing personas by ID
  personas: Persona[];
  isLoadingAvatars: boolean;

  // Actions
  handlePersonaSelect: (persona: Persona) => void;
  handleDeleteMessage: (id: string) => void;
  resetState: () => void;
  isUploadingCCFile: boolean;
}

const AnalyzeFileContext = createContext<AnalyzeFileContextValue | undefined>(undefined);

interface AnalyzeFileProviderProps {
  children: ReactNode;
  open: boolean;
  file: FormattedFile;
  onAfterCCFileUpload?: (file: FormattedFile) => void;
  entityRecord: EntityRecord;
}

export const AnalyzeFileProvider = ({ children, open, file: propFile, onAfterCCFileUpload, entityRecord }: AnalyzeFileProviderProps) => {
  const [localFile, setLocalFile] = useState<FormattedFile>(propFile);
  const fileId = localFile.id;
  const { user, project } = useSwivuBryntumContext();
  const [messages, setMessages] = useState<InsightMessage[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [typingPersona, setTypingPersona] = useState<Persona | null>(null);
  const [typingPersonas, setTypingPersonas] = useState<Set<string>>(new Set());
  const [personaAvatars, setPersonaAvatars] = useState<Record<string, string | null>>({});
  const [isLoadingAvatars, setIsLoadingAvatars] = useState<boolean>(true);
  const activeRequestsRef = useRef<Map<string, AbortController>>(new Map());
  // Track request parameters for cancellation
  const activeRequestParamsRef = useRef<Map<string, { file_id: number; insight_type: string }>>(new Map());
  const [isUploadingCCFile, setIsUploadingCCFile] = useState<boolean>(false);
  const { getInsights, generateInsight, deleteInsight, getInsightTypesAvatar, cancelInsight } = useFileInsights();

  // Subscribe to store changes for reactive updates
  const { version } = useSwivuStore({ storeId: STORE_KEYS.insightsStore });

  // Create personas from INSIGHT_TYPES
  const personas = useMemo<Persona[]>(() => {
    return Object.values(INSIGHT_TYPES).map((insightType) => ({
      id: insightType.value,
      name: insightType.label,
      icon: INSIGHT_TYPE_ICONS[insightType.value] || Code,
      description: insightType.category,
      color: INSIGHT_TYPE_COLORS[insightType.value] || "bg-gray-500",
      avatarUrl: personaAvatars[insightType.value]
    }));
  }, [personaAvatars]);

  const handleCCFiles = async () => {
    if (!localFile.originalImageUrl || isUploadingCCFile || !project) return false;
    setIsUploadingCCFile(true);
    try {
      const response = await fetch(localFile.originalImageUrl);
      const blob = await response.blob();

      const fileName = `${localFile.name}.${getFileExtension(localFile.originalImageUrl)}`;
      const { data: uploadedFile } = await uploadFile(blob, fileName);

      const newFileRecord = project.boardFilesStore.createRecord({
        name: fileName,
        documentId: uploadedFile.id,
        [entityRecord.entityType]: entityRecord.id,
      }) as unknown as BoardFile;

      project.boardFilesStore.add(newFileRecord);

      await project.sync();

      const [formattedFile] = formatFile([newFileRecord], user);
      setLocalFile(formattedFile);
      setIsUploadingCCFile(false);
      onAfterCCFileUpload?.(formattedFile);
      return newFileRecord;
    } catch (error) {
      console.error("[AnalyzeFileContext] Failed to fetch CC file:", error);
      toast.error("Failed to fetch & upload cc image file");
      setIsUploadingCCFile(false);
      return false;
    }
  }

  useEffect(() => {
    if (propFile) {
      setLocalFile(propFile);
    }
  }, [propFile]);

  // Fetch avatars for all insight types when component mounts
  useEffect(() => {
    if (!open) {
      setIsLoadingAvatars(false);
      return;
    }

    const fetchAvatars = async () => {
      setIsLoadingAvatars(true);
      try {
        const avatarPromises = Object.values(INSIGHT_TYPES).map(async (insightType) => {
          const avatarUrl = await getInsightTypesAvatar(insightType.value);
          return { type: insightType.value, url: avatarUrl };
        });

        const results = await Promise.all(avatarPromises);
        const avatarMap: Record<string, string | null> = {};
        results.forEach(({ type, url }) => {
          avatarMap[type] = url;
        });
        setPersonaAvatars(avatarMap);
      } catch (error) {
        console.error("[AnalyzeFileContext] Failed to fetch avatars:", error);
      } finally {
        setIsLoadingAvatars(false);
      }
    };

    fetchAvatars();
  }, [open, getInsightTypesAvatar]);

  // Load existing insights when dialog opens, fileId changes, or store updates
  useEffect(() => {
    if (!open || !fileId) {
      setMessages([]);
      return;
    }

    const insights = getInsights(Number(fileId));
    const insightMessages: InsightMessage[] = insights.map((insight: Insights) => ({
      id: String(insight.id),
      personaId: insight.insightType,
      personaName: INSIGHT_TYPES[insight.insightType as keyof typeof INSIGHT_TYPES]?.label || insight.insightType,
      content: insight.content || "",
      timestamp: insight.createdAt ? new Date(insight.createdAt) : new Date()
    }));

    // Sort by timestamp (newest first)
    insightMessages.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    // Remove typing indicators for personas that now have messages
    setTypingPersonas((prev) => {
      const newSet = new Set(prev);
      insightMessages.forEach((msg) => {
        newSet.delete(msg.personaId);
      });
      return newSet;
    });

    setMessages(insightMessages);
  }, [open, fileId, version, getInsights]);

  const handlePersonaSelect = useCallback(
    async (persona: Persona) => {

      let fileId = localFile.id;
      if (localFile.isCCImage) {
        const newFileRecord = await handleCCFiles();
        if (newFileRecord) {
          fileId = newFileRecord.id;
        }
      }

      if (!fileId) return;

      // Check if there's already an active request for this persona
      if (activeRequestsRef.current.has(persona.id)) {
        console.warn(`[AnalyzeFileContext] Insight generation already in progress for "${persona.name}"`);
        return;
      }

      // Create an AbortController for this request
      const abortController = new AbortController();
      activeRequestsRef.current.set(persona.id, abortController);

      // Store request parameters for cancellation
      activeRequestParamsRef.current.set(persona.id, {
        file_id: Number(fileId),
        insight_type: persona.id
      });

      setSelectedPersonaId(persona.id);
      setIsTyping(true);
      setTypingPersona(persona);

      // Add persona to typing set
      setTypingPersonas((prev) => new Set(prev).add(persona.id));

      try {
        // Generate insight via API with English as default language, passing the abort signal
        await generateInsight(Number(fileId), persona.id, INSIGHT_LANGUAGE.english.value, abortController.signal);
        // The store will update via useSwivuStore subscription, which will trigger the useEffect above
        // The typing indicator will be removed when the new insight appears in the messages
      } catch (error: any) {
        // Only log error if it's not an abort error
        if (error?.name !== 'AbortError' && error?.message !== 'canceled') {
          console.error("[AnalyzeFileContext] Failed to generate insight:", error);
        }
        // Remove from typing set on error (but not if it was just aborted on close)
        if (error?.name !== 'AbortError' && error?.message !== 'canceled') {
          setTypingPersonas((prev) => {
            const newSet = new Set(prev);
            newSet.delete(persona.id);
            return newSet;
          });
        }
      } finally {
        setIsTyping(false);
        setTypingPersona(null);
        // Clean up the abort controller and params
        activeRequestsRef.current.delete(persona.id);
        activeRequestParamsRef.current.delete(persona.id);
      }
    },
    [fileId, generateInsight, localFile]
  );

  const handleDeleteMessage = useCallback(
    (id: string) => {
      deleteInsight(id);
      // The store will update via useSwivuStore subscription, which will trigger the useEffect above
    },
    [deleteInsight]
  );

  const resetState = useCallback(() => {
    setMessages([]);
    setSelectedPersonaId(null);
    setIsTyping(false);
    setTypingPersona(null);
    setTypingPersonas(new Set());
    setIsLoadingAvatars(true);
  }, []);

  // Cancel all active requests and reset state when dialog closes
  useEffect(() => {
    if (!open) {
      // Cancel all active requests
      activeRequestsRef.current.forEach((controller, personaId) => {
        console.log(`[AnalyzeFileContext] Canceling pending request for persona: ${personaId}`);
        controller.abort();

        // Notify backend about cancellation
        const params = activeRequestParamsRef.current.get(personaId);
        if (params) {
          cancelInsight(params.file_id, params.insight_type);
        }
      });
      activeRequestsRef.current.clear();
      activeRequestParamsRef.current.clear();

      // Small delay to allow animations to complete
      setTimeout(() => {
        resetState();
      }, 300);
    }
  }, [open, resetState, cancelInsight]);

  const value: AnalyzeFileContextValue = {
    messages,
    selectedPersonaId,
    isTyping,
    typingPersona,
    typingPersonas,
    personas,
    isLoadingAvatars,
    handlePersonaSelect,
    handleDeleteMessage,
    resetState,
    isUploadingCCFile
  };

  return <AnalyzeFileContext.Provider value={value}>{children}</AnalyzeFileContext.Provider>;
};

export const useAnalyzeFileContext = () => {
  const context = useContext(AnalyzeFileContext);
  if (context === undefined) {
    throw new Error("useAnalyzeFileContext must be used within an AnalyzeFileProvider");
  }
  return context;
};
